import React, { useState, useRef } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import jsPDF from "jspdf";
import html2pdf from "html2pdf.js";
import "jspdf-autotable";
import Swal from "sweetalert2"; 

const BASE_URL = "https://ricgop.online/backend";

export default function RosterForm() {
  const [month, setMonth] = useState("");
  const [labRange, setLabRange] = useState("A-D");
  const labs = ["Lab A", "Lab B", "Lab C", "Lab D", "Lab E"];
  const weekDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  const [days, setDays] = useState([]);
  const [selectedLabs, setSelectedLabs] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loadingRoster, setLoadingRoster] = useState(false);
  const [generatingRoster, setGeneratingRoster] = useState(false);




  const [rows, setRows] = useState([]);

  // autocomplete state
  const [suggestions, setSuggestions] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [activeCell, setActiveCell] = useState(null);
  const inputRefs = useRef({});

  const normalizeValue = (raw) =>
    raw.replace(/,\s*/g, ", ").replace(/\s{2,}/g, " ").trim();

  const updateValue = (dayIndex, field, rawValue) => {
    const value = normalizeValue(rawValue);
    const updated = [...days];
    updated[dayIndex][field] = value;
    setDays(updated);

    const lastToken = value.split(/[\s,]+/).pop().trim();
    if (lastToken.length >= 1) {
      axios
        .get(`${BASE_URL}/get_names.php`, { params: { name: lastToken } })
        .then((res) => setSuggestions(Array.isArray(res.data) ? res.data : []))
        .catch(() => setSuggestions([]));
    } else {
      setSuggestions([]);
    }

    setHighlightedIndex(-1);
    setActiveCell({ dayIndex, field });
  };

  const handleKeyDown = (e, dayIndex, field) => {
    if (!suggestions.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((p) => (p + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((p) => (p <= 0 ? suggestions.length - 1 : p - 1));
    } else if (e.key === "Enter" && highlightedIndex >= 0) {
      e.preventDefault();
      insertSuggestion(suggestions[highlightedIndex], dayIndex, field);
    } else if (e.key === "Escape") {
      setSuggestions([]);
      setHighlightedIndex(-1);
    }
  };

  const insertSuggestion = (name, dayIndex, field) => {
    const key = `${dayIndex}-${field}`;
    const input = inputRefs.current[key];
    const currentText = days[dayIndex][field] || "";
    const cursor = input?.selectionStart || currentText.length;
    const match = currentText.slice(0, cursor).match(/[\w\s]*$/);
    const insertStart = match ? cursor - (match[0]?.length || 0) : cursor;
    const textBefore = currentText.slice(0, insertStart);
    const textAfter = currentText.slice(cursor);
    const separator =
      textBefore.trim().endsWith(",") || textBefore.trim() === "" ? "" : ", ";
    const newText = (textBefore + separator + name + textAfter).replace(/,\s*/g, ", ");
    updateValue(dayIndex, field, newText);
    setTimeout(() => input?.focus(), 0);
    setSuggestions([]);
    setHighlightedIndex(-1);
  };

  const generateDays = () => {
  if (!month) {
    Swal.fire("⚠️ Please select a month first!");
    return;
  }
  if (!year) {
    Swal.fire("⚠️ Please select a year first!");
    return;
  }
  if (!labRange) {
    Swal.fire("⚠️ Please select lab range!");
    return;
  }

  setGeneratingRoster(true);

  try {
    const [startLetter, endLetter] = labRange.split("-");
    const startIndex = labs.findIndex((l) => l.endsWith(startLetter));
    const endIndex = labs.findIndex((l) => l.endsWith(endLetter));

    if (startIndex === -1 || endIndex === -1 || startIndex > endIndex) {
      Swal.fire("⚠️ Invalid lab range selection.");
      return;
    }

    const selLabs = labs.slice(startIndex, endIndex + 1);
    setSelectedLabs(selLabs);

    const temp = weekDays.map((dayName) => ({
      day: dayName,
      list_incharge: "",
      admin_incharge: "",
      assignments: selLabs.map((labName) => ({
        lab_name: labName,
        assigned_doctor: "",
      })),
    }));

    setDays(temp);

    Swal.fire({
      icon: "success",
      title: "✅ Roster generated successfully",
      timer: 2000,
      showConfirmButton: false,
    });
  } finally {
    setGeneratingRoster(false);
  }
};


const saveRoster = async () => {
  // Validate month, year, lab range
  if (!month) {
    return Swal.fire({ icon: "warning", title: "Select a month first!", timer: 2000, showConfirmButton: false });
  }
  if (!year) {
    return Swal.fire({ icon: "warning", title: "Select a year first!", timer: 2000, showConfirmButton: false });
  }
  if (!labRange) {
    return Swal.fire({ icon: "warning", title: "Select lab range!", timer: 2000, showConfirmButton: false });
  }
  if (!days.length) {
    return Swal.fire({ icon: "warning", title: "No roster data to save!", timer: 2000, showConfirmButton: false });
  }

  // Validate each textarea in the roster table
  for (let day of days) {
    // Lab assignments
    for (let assignment of day.assignments) {
      if (!assignment.assigned_doctor || assignment.assigned_doctor.toString().trim() === "") {
        return Swal.fire({
          icon: "warning",
          title: "Please fill all lab assignments!",
          text: `Missing doctor in ${day.day}`,
        });
      }
    }
    // List Incharge
    if (!day.list_incharge || day.list_incharge.toString().trim() === "") {
      return Swal.fire({
        icon: "warning",
        title: "List Incharge is required!",
        text: `Missing list incharge in ${day.day}`,
      });
    }
    // Admin Incharge
    if (!day.admin_incharge || day.admin_incharge.toString().trim() === "") {
      return Swal.fire({
        icon: "warning",
        title: "Admin Incharge is required!",
        text: `Missing admin incharge in ${day.day}`,
      });
    }
  }

  // If all validation passes, save roster
  try {
    Swal.fire({ title: "Saving roster...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });

    const res = await axios.post(`${BASE_URL}/cath_lab_roster.php`, {
      action: "save_roster",
      month,
      year,
      lab_range: labRange,
      days,
    });

    Swal.close();

    if (res.data.success) {
      Swal.fire({ icon: "success", title: "Roster saved successfully!", timer: 2000, showConfirmButton: false });
    } else {
      Swal.fire({ icon: "error", title: "Save failed", text: res.data.error || "Unknown error" });
    }
  } catch (err) {
    Swal.close();
    console.error(err);
    Swal.fire({ icon: "error", title: "Save failed", text: "Check console for details." });
  }
};



  // ✅ Auto translate to Urdu
const autoTranslateToUrdu = async (text) => {
  if (!text?.trim()) return text;
  try {
    const res = await fetch(
      `https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=ur&dt=t&q=${encodeURIComponent(
        text
      )}`
    );
    const json = await res.json();
    return json[0]?.map((item) => item[0]).join("") || text;
  } catch (err) {
    console.error("Translation error:", err);
    return text;
  }
};

// ✅ Fancy PDF Generator (dynamic labs, English + Urdu)
const generateFancyPDF = async ({ language = "en", month, days, selectedLabs, download = true }) => {
  const isUrdu = language === "ur";

  const hospitalName = isUrdu
    ? "راولپنڈی انسٹیٹیوٹ آف کارڈیالوجی"
    : "Rawalpindi Institute of Cardiology";
  const subAddress = isUrdu ? "راول روڈ، راولپنڈی" : "Rawal Road, Rawalpindi";

  const title = isUrdu ? "کیتھ لیب روسٹر" : "Cath Lab Roster";
  const subTitle = isUrdu ? "ماہانہ کیتھ لیب شیڈول" : "Monthly Cath Lab Schedule";

  const leftLogoUrl = "../../assets/img/logo/new-logo.png";
  const rightLogoUrl = "../../assets/img/logo/punjab-logo.png";

  let translatedLabs = selectedLabs;
  if (isUrdu) {
    translatedLabs = await Promise.all(
      selectedLabs.map((lab) => autoTranslateToUrdu(lab))
    );
  }

  const allHeaders = [
    isUrdu ? "دن" : "Day",
    ...translatedLabs,
    isUrdu ? "لسٹ انچارج" : "List Incharge",
    isUrdu ? "ایڈمن انچارج" : "Admin Incharge",
  ];

  const headers = allHeaders.map((h) => `<th style="padding: 10px;">${h}</th>`).join("");

  const bodyRows = await Promise.all(
    days.map(async (day, index) => {
      const bg = index % 2 === 0 ? "#f4f6fb" : "#ffffff";

      // Ensure arrays
      const assignments = day.assignments || [];
      const listIncharge = Array.isArray(day.list_incharge)
        ? day.list_incharge
        : day.list_incharge
        ? [day.list_incharge]
        : [];
      const adminIncharge = Array.isArray(day.admin_incharge)
        ? day.admin_incharge
        : day.admin_incharge
        ? [day.admin_incharge]
        : [];

      const rowCells = [
        isUrdu ? await autoTranslateToUrdu(day.day) : day.day,

        // Lab assignments
        ...(await Promise.all(
          assignments.map(async (a) => {
            const doctorsArray = Array.isArray(a.assigned_doctor)
              ? a.assigned_doctor
              : a.assigned_doctor
              ? [a.assigned_doctor]
              : [];
            const formattedDoctors = await Promise.all(
              doctorsArray.map(async (d) => (isUrdu ? await autoTranslateToUrdu(d) : d))
            );
            return formattedDoctors.join("<br />");
          })
        )),

        // List Incharge
        (await Promise.all(
          listIncharge.map(async (d) => (isUrdu ? await autoTranslateToUrdu(d) : d))
        )).join("<br />"),

        // Admin Incharge
        (await Promise.all(
          adminIncharge.map(async (d) => (isUrdu ? await autoTranslateToUrdu(d) : d))
        )).join("<br />"),
      ];

      return `<tr style="background-color:${bg};">${rowCells
        .map((c) => `<td style="padding:5px; vertical-align:top;">${c}</td>`)
        .join("")}</tr>`;
    })
  );

  const content = `
    <div style="font-family: ${isUrdu ? "'Noto Nastaliq Urdu'" : "'Segoe UI', sans-serif"};
      direction: ${isUrdu ? "rtl" : "ltr"}; padding: 20px; font-size: 13px; color: #2c3e50;">
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;">
        <div style="width: 25%; display: flex; justify-content: center;">
          <img src="${leftLogoUrl}" style="max-height: 100px; width: auto;" />
        </div>
        <div style="text-align: center; width: 50%;">
          <h2 style="margin: 0; font-weight: 700;">${hospitalName}</h2>
          <p style="margin: 2px 0; font-size: 13px; color: #555;">${subAddress}</p>
          <h4 style="margin: 6px 0; color: #444;">${title} – ${month}</h4>
          <p style="margin: 2px 0; font-size: 14px; font-weight: 600; color: #333;">${subTitle}</p>
        </div>
        <div style="width: 25%; display: flex; justify-content: center;">
          <img src="${rightLogoUrl}" style="max-height: 100px; width: auto;" />
        </div>
      </div>

     
      <table style="width: 100%; border-collapse: collapse; font-size: 10px; text-align: center;">
        <thead>
          <tr style="background: linear-gradient(to right, #0052D4, #4364F7); color: #fff;">
            ${headers}
          </tr>
        </thead>
        <tbody>
          ${bodyRows.join("")}
        </tbody>
      </table>

      <div style="margin-top: 80px; display: flex; justify-content: center; gap: 80px; font-size: 13px;">
        <div style="text-align: center; line-height: 1.5;">
           <div style="border-top: 1px solid #333; width: 220px; margin: auto; margin-bottom: 6px;"></div>
          <strong>Dr. Hamza Azhar Ghauri</strong><br />
          <span>${isUrdu ? "اسسٹنٹ پروفیسر / روسٹر انچارج" : "Assistant Professor / Roster Incharge"}</span><br />
          <span>${hospitalName}</span>
        </div>
        <div style="text-align: center; line-height: 1.5;">
          <div style="border-top: 1px solid #333; width: 220px; margin: auto; margin-bottom: 6px;"></div>
          <strong>Dr. Asim Javed</strong><br />
          <span>${isUrdu ? "ایسوسی ایٹ پروفیسر / ایچ او ڈی کارڈیالوجی" : "Associate Professor / HOD Cardiology"}</span><br />
          <span>${hospitalName}</span>
        </div>
      </div>

      <p style="text-align:center; margin-top: 40px; font-size: 10px; color: #888;">
        © ${new Date().getFullYear()} ${hospitalName}. All rights reserved.
      </p>
    </div>
  `;

  const opt = {
    margin: 0.4,
    filename: `CathLab_Roster_${month}_${language.toUpperCase()}.pdf`,
    image: { type: "jpeg", quality: 0.98 },
    html2canvas: { scale: 2 },
    jsPDF: { unit: "in", format: "a4", orientation: "portrait" }, // portrait A4
  };

  if (download) {
    await html2pdf().from(content).set(opt).save();
    return null;
  } else {
    return await html2pdf().from(content).set(opt).outputPdf("blob");
  }
};

  

  const handleAddRow = () => {
    setRows([...rows, { date: "", presenter: "", supervisor: "" }]);
  };

  const handleChange = (i, field, value) => {
    const updated = [...rows];
    updated[i][field] = value;
    setRows(updated);
  };

  // ✅ Generate both PDFs
const generateBothPDFs = async () => {
  if (!days.length) {
    return Swal.fire({
      icon: "warning",
      title: "⚠️ Please generate roster first",
      timer: 2000,
      showConfirmButton: false,
    });
  }

  // Show loading Swal while generating PDFs
  Swal.fire({
    title: "Generating PDFs...",
    text: "Please wait",
    allowOutsideClick: false,
    didOpen: () => {
      Swal.showLoading();
    },
  });

  try {
    await generateFancyPDF({ language: "en", month, days, selectedLabs, download: true });
    await generateFancyPDF({ language: "ur", month, days, selectedLabs, download: true });

    Swal.close(); // Close loading

    Swal.fire({
      icon: "success",
      title: "✅ PDFs generated successfully!",
      timer: 2000,
      showConfirmButton: false,
    });
  } catch (err) {
    Swal.close(); // Close loading
    console.error(err);
    Swal.fire({
      icon: "error",
      title: "❌ PDF generation failed",
      text: "Check console for details.",
    });
  }
};


const emailRoster = async () => {
  if (!days.length) {
    return Swal.fire({
      icon: "warning",
      title: "⚠️ Please generate roster first",
      timer: 2000,
      showConfirmButton: false,
    });
  }

  // Show a loading Swal while generating PDFs
  Swal.fire({
    title: "Generating PDFs...",
    text: "Please wait",
    allowOutsideClick: false,
    didOpen: () => {
      Swal.showLoading();
    },
  });

  try {
    const englishBlob = await generateFancyPDF({ language: "en", month, days, selectedLabs, download: false });
    const urduBlob = await generateFancyPDF({ language: "ur", month, days, selectedLabs, download: false });

    const formData = new FormData();
    formData.append("month", month);
    formData.append("englishPDF", new File([englishBlob], `CathLab_Roster_${month}_EN.pdf`));
    formData.append("urduPDF", new File([urduBlob], `CathLab_Roster_${month}_UR.pdf`));

    const res = await fetch("https://ricgop.online/backend/email_cathlab_roster.php", { method: "POST", body: formData });
    const data = await res.json();

    Swal.close(); // close loading

    if (data.success) {
      Swal.fire({
        icon: "success",
        title: "✅ Roster emailed successfully!",
        timer: 2000,
        showConfirmButton: false,
      });
    } else {
      Swal.fire({
        icon: "error",
        title: "❌ Email failed",
        text: data.error || "Something went wrong",
      });
    }
  } catch (err) {
    Swal.close(); // close loading
    console.error(err);
    Swal.fire({
      icon: "error",
      title: "⚠️ Email sending error",
      text: "Check console for details.",
    });
  }
};




const loadRoster = async () => {
  if (!month || !year) {
    Swal.fire("⚠️ Please select month and year first");
    return;
  }

  setLoadingRoster(true);

  try {
    const res = await axios.post(`${BASE_URL}/cath_lab_roster.php`, {
      action: "load_roster",
      month,
      year,
    });

    if (res.data.success) {
      const roster = res.data.roster;
      setLabRange(roster.lab_range || "A-D");
      setDays(roster.days || []);
      const selectedLabsArray = roster.days[0]?.assignments.map(a => a.lab_name) || [];
      setSelectedLabs(selectedLabsArray);

      Swal.fire({
        icon: "success",
        title: "✅ Roster loaded successfully",
        timer: 2000,
        showConfirmButton: false,
      });
    } else {
      setDays([]);
      setSelectedLabs([]);
      Swal.fire({
        icon: "info",
        title: "ℹ️ No roster found for this month/year",
        timer: 2500,
        showConfirmButton: false,
      });
    }
  } catch (err) {
    console.error(err);
    Swal.fire("❌ Failed to load roster. See console for details.");
  } finally {
    setLoadingRoster(false);
  }
};




  return (
    <div className="roster-wrapper">
      <div className="roster-container">
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="roster-title"
        >
          Cath Lab Monthly Roster
        </motion.h2>

{/* Filters Section */}
<div className="roster-filters">
  {/* Month Selector */}
  <select value={month} onChange={(e) => setMonth(e.target.value)}>
    <option value="">Select Month</option>
    {[...Array(12)].map((_, i) => {
      const monthName = new Date(0, i).toLocaleString("default", { month: "long" });
      return (
        <option key={monthName} value={monthName}>
          {monthName}
        </option>
      );
    })}
  </select>

  {/* Year Selector */}
  <select value={year} onChange={(e) => setYear(e.target.value)}>
    <option value="">Select Year</option>
    {Array.from({ length: 10 }, (_, i) => {
      const y = new Date().getFullYear() - 5 + i; // 5 years back + next 5 years
      return (
        <option key={y} value={y}>
          {y}
        </option>
      );
    })}
  </select>

  {/* Lab Range Selector */}
  <select value={labRange} onChange={(e) => setLabRange(e.target.value)}>
    <option value="">Select Lab</option>
    <option value="A-A">Lab A</option>
    <option value="A-B">Lab A–B</option>
    <option value="A-C">Lab A–C</option>
    <option value="A-D">Lab A–D</option>
    <option value="A-E">Lab A–E</option>
  </select>

  {/* Generate Roster Button */}
  <button
    onClick={generateDays}
    className="btn btn-generate"
    disabled={generatingRoster}
  >
    {generatingRoster ? "Generating..." : "Generate Roster"}
  </button>

  {/* Load Roster Button */}
  <button
    onClick={loadRoster}
    className="btn btn-generate"
    disabled={loadingRoster}
  >
    {loadingRoster ? "Loading..." : "🔄 Load Roster"}
  </button>

  {/* Generate PDF Button */}
  <button onClick={generateBothPDFs} className="btn btn-generate">
    📄 Generate PDF
  </button>

  {/* Email Roster Button */}
  <button onClick={emailRoster} className="btn btn-generate">
    📧 Email Roster
  </button>
</div>




        {/* Table */}
        {days.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="roster-table-container"
          >
            <table className="roster-table">
              <thead>
                <tr>
                  <th>Day</th>
                  {selectedLabs.map((lab, i) => (
                    <th key={i}>{lab}</th>
                  ))}
                  <th>List Incharge</th>
                  <th>Admin In-charge</th>
                </tr>
              </thead>
              <tbody>
                {days.map((day, i) => (
                  <tr key={i}>
                    <td>{day.day}</td>
                    {day.assignments.map((a, j) => (
                      <td key={j}>
                        <textarea
                          placeholder="Doctor(s) / Notes"
                          value={a.assigned_doctor}
                          onChange={(e) => {
                            const newDays = [...days];
                            newDays[i].assignments[j].assigned_doctor = e.target.value;
                            setDays(newDays);
                          }}
                        />
                      </td>
                    ))}
                    <td>
                      <textarea
                        placeholder="List Incharge / Notes"
                        value={day.list_incharge}
                        onChange={(e) => {
                          const newDays = [...days];
                          newDays[i].list_incharge = e.target.value;
                          setDays(newDays);
                        }}
                      />
                    </td>
                    <td>
                      <textarea
                        placeholder="Admin Incharge / Notes"
                        value={day.admin_incharge}
                        onChange={(e) => {
                          const newDays = [...days];
                          newDays[i].admin_incharge = e.target.value;
                          setDays(newDays);
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        )}

        {/* Actions */}
        {days.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="roster-actions"
          >
            <button onClick={saveRoster} className="btn btn-save">
              💾 Save Roster
            </button>
            <button
              onClick={() => {
                setDays([]);
                setSelectedLabs([]);
              }}
              className="btn btn-clear"
            >
              ❌ Clear
            </button>
          </motion.div>
        )}
      </div>

      {/* Inline CSS */}
      <style>{`
        .roster-wrapper {
          padding: 2rem;
          background: linear-gradient(to bottom right, #f8fafc, #f1f5f9);
          min-height: 100vh;
        }
        .roster-container {
          max-width: 1200px;
          margin: 0 auto;
        }
        .roster-title {
          font-size: 2.5rem;
          font-weight: bold;
          margin-bottom: 2.5rem;
          text-align: center;
          color: #1e293b;
        }
        .roster-filters {
          display: flex;
          gap: 1rem;
          justify-content: center;
          margin-bottom: 2rem;
          padding: 1.5rem;
          background: #fff;
          border-radius: 1rem;
          box-shadow: 0 2px 6px rgba(0,0,0,0.1);
          flex-wrap: wrap;
        }
        .roster-filters select,
        .roster-filters button {
          padding: 0.5rem 1rem;
          border-radius: 0.5rem;
          border: 1px solid #cbd5e1;
          font-size: 0.95rem;
        }
        .btn {
          cursor: pointer;
          font-weight: 600;
          color: #fff;
          border: none;
          transition: background 0.3s;
        }
        .btn-generate {
          background: #2563eb;
        }
        .btn-generate:hover {
          background: #1d4ed8;
        }
        .btn-save {
          background: #16a34a;
          padding: 0.8rem 2rem;
          border-radius: 0.75rem;
          font-size: 1rem;
        }
        .btn-save:hover {
          background: #15803d;
        }
        .btn-clear {
          background: #dc2626;
          padding: 0.8rem 2rem;
          border-radius: 0.75rem;
          font-size: 1rem;
        }
        .btn-clear:hover {
          background: #b91c1c;
        }
        .roster-table-container {
          background: #fff;
          border-radius: 1rem;
          overflow-x: auto;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }
        .roster-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.95rem;
        }
        .roster-table th {
          background: #2563eb;
          color: #fff;
          padding: 1rem;
          text-align: left;
        }
        .roster-table td {
          border-top: 1px solid #e2e8f0;
          padding: 0.75rem;
          vertical-align: top;
        }
        .roster-table tr:nth-child(even) {
          background: #f8fafc;
        }
        textarea {
          width: 100%;
          min-height: 6rem;
          padding: 0.5rem;
          border-radius: 0.5rem;
          border: 1px solid #cbd5e1;
          font-size: 0.9rem;
          resize: vertical;
          outline: none;
          transition: border 0.3s, box-shadow 0.3s;
        }
        textarea:focus {
          border-color: #2563eb;
          box-shadow: 0 0 0 2px rgba(37,99,235,0.2);
        }
        .roster-actions {
          display: flex;
          justify-content: center;
          gap: 1.5rem;
          margin-top: 2.5rem;
        }
      `}</style>
    </div>
  );
}
